// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  ButtonGroup,
  type ButtonGroupProps,
  type Description,
  LgSpacing,
  Row,
  SecondaryPage,
  XsSpacing,
} from '@alto/design-system';
import React, { type ComponentProps } from 'react';
import { Platform, StyleSheet, View } from 'react-native';

type SecondaryPageProps = ComponentProps<typeof SecondaryPage>;

type Props = Omit<SecondaryPageProps, 'buttons' | 'Footnote' | 'inlineButtons' | 'justifyButtons'> & {
  readonly buttons?: ButtonGroupProps['buttons'];
  readonly Footnote?: React.ReactElement<typeof Description>;
  readonly justifyButtons?: ButtonGroupProps['justifyButtons'];
  readonly inlineButtons?: ButtonGroupProps['inline'];
};

const IS_WEB = Platform.OS === 'web';
// flex:1 so SecondaryPage's flex-grow PageRoot still fills the screen, plus a 16px top inset
// between the apps/web sticky alto header and the SecondaryPage NavBar (per design).
const styles = StyleSheet.create({ fill: { flex: 1 } });

export const ProgynyPage = ({
  buttons,
  Footnote,
  justifyButtons,
  inlineButtons = false,
  headerBackgroundColor = COLORS.BACKGROUND_COLORS.PINK_LIGHTEST,
  children,
  ...rest
}: Props) => {
  const hasButtons = !!buttons && buttons.length > 0;

  // On native, render SecondaryPage directly (no wrapper) with its `buttons` prop, so the
  // buttons stay anchored at the bottom via AnchoredButtonGroup — identical to the layout
  // before this wrapper existed. The View wrapper and inline-buttons logic below are only
  // applied on web, where the AnchoredButtonGroup gets hidden behind the apps/web page footer.
  if (!IS_WEB) {
    return (
      <SecondaryPage
        {...rest}
        headerBackgroundColor={headerBackgroundColor}
        buttons={hasButtons ? buttons : undefined}
        Footnote={Footnote}
        justifyButtons={justifyButtons}
        inlineButtons={inlineButtons}
      >
        {children}
      </SecondaryPage>
    );
  }

  return (
    <View style={[styles.fill, { backgroundColor: headerBackgroundColor }]}>
      <SecondaryPage
        {...rest}
        headerBackgroundColor={headerBackgroundColor}
      >
        {children}
        {Footnote ? (
          <>
            <LgSpacing />
            <Row center>{Footnote}</Row>
            <XsSpacing />
          </>
        ) : null}
        {hasButtons ? (
          <>
            {!Footnote ? <LgSpacing /> : null}
            <ButtonGroup
              rowReversed
              buttons={buttons}
              inline={inlineButtons}
              justifyButtons={justifyButtons}
            />
          </>
        ) : null}
      </SecondaryPage>
    </View>
  );
};
